<template>
  <div id="edit-textbaustein-view">
    <div v-if="textbausteinid != '0' && textbaustein == null" class="alert alert-danger">
      Der Textbaustein konnte nicht gefunden werden.

      <button type="button" class="btn btn-secondary me-3" @click="cancel()">Zurück</button>
    </div>

    <div class="with-right-sidebar">
      <div class="d-flex justify-content-between">
        <h1 v-if="textbausteinid == '0'" class="fs-3">Neuer Textbaustein</h1>
        <h1 v-else class="fs-3">{{ editTextbaustein.name }} bearbeiten</h1>
      </div>

      <section id="edit-textbaustein">
        <div class="mx-1 textbaustein-abschnitt-bg shadow-sm border px-2 py-3">
          <TextbausteinContentBearbeiten :textbaustein="editTextbaustein" :originalTextbaustein="textbaustein" />
        </div>

        <div class="mt-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <strong>Abschnitte</strong>

            <!-- <TextbausteinAbschnitteRegelnModal :index="0" :textbaustein="editTextbaustein" /> -->
          </div>

          <div
            v-for="(abschnitt, index) in editTextbaustein.abschnitte.sort((a: Textbaustein, b: Textbaustein) => a.position - b.position)"
            :key="index"
            class="mb-3 textbaustein-abschnitt-bg shadow-sm border"
          >
            <div class="row mx-0 mb-3 border-bottom">
              <div v-if="editTextbaustein.abschnitte.length > 1" class="col p-0 d-flex">
                <button type="button" class="btn btn-sm border-0" @click="abschnittPosUp(index)" :disabled="abschnitt.position <= 1">
                  <font-awesome-icon icon="chevron-up" />
                </button>

                <button
                  type="button"
                  class="btn btn-sm border-0"
                  @click="abschnittPosDown(index)"
                  :disabled="abschnitt.position == editTextbaustein.abschnitte.length"
                >
                  <font-awesome-icon icon="chevron-down" />
                </button>
              </div>

              <div v-else class="col"></div>

              <div class="col text-end">
                <button type="button" class="btn" @click="removeAbschnitt(index)"><font-awesome-icon icon="trash" /></button>
              </div>
            </div>

            <div class="p-3">
              <TextbausteinContentBearbeiten :textbaustein="abschnitt" :originalTextbaustein="textbaustein" :index="index" />
            </div>
          </div>

          <button type="button" class="btn btn-primary" @click="addAbschnitt()"><font-awesome-icon icon="plus" /> Abschnitt hinzufügen</button>
        </div>

        <LoadingSpinner v-if="loading" text="... Daten werden geladen ..." />

        <div v-else-if="errorMsg != ''" class="alert alert-danger mt-5">
          {{ errorMsg }}
          <!-- Es ist ein Fehler beim Laden der Vorlagen aufgetreten. Bite probieren Sie es später erneut. -->
        </div>

        <div v-if="ownErrorMsg != ''" class="alert alert-danger mt-5">
          {{ ownErrorMsg }}
          <!-- Es ist ein Fehler beim Laden der Vorlagen aufgetreten. Bite probieren Sie es später erneut. -->
        </div>

        <div class="save-bar bg-white position-fixed bottom-0 end-0 p-3 w-100 shadow border-top d-flex justify-content-end">
          <button type="button" class="btn btn-secondary me-5" @click="cancel()">Abbrechen</button>

          <button v-if="textbausteinid != '0'" type="submit" class="btn btn-outline-primary me-3" @click="createOrUpdateTextbaustein(false)">Speichern</button>

          <button type="submit" class="btn btn-primary" @click="createOrUpdateTextbaustein(true)">
            <span v-if="textbausteinid != '0'">Speichern</span>
            <span v-else>Erstellen</span>
            und Schließen
          </button>
        </div>

        <hr class="my-4" />

        <button class="btn btn-danger" @click="deleteTextbausteinRequest()"><font-awesome-icon icon="trash" /> Textbaustein löschen</button>
      </section>
    </div>

    <SelectVariablenSidebar />

    <!-- <ConfirmationAlert ref="confirmationAlertRef" @confirm="deleteTextbaustein" /> -->
    <ConfirmationAlert ref="confirmationAlertRef" @confirm="onConfrim" />
    <LoadingOverlay v-if="loading" />
  </div>
</template>

<script lang="ts" setup>
  //#region Imports
  import { computed, ref, PropType, onMounted } from 'vue';
  import { useStore } from '@/store';
  import { useRouter } from 'vue-router';

  import { ActionTypes as VorlageActionTypes } from '@/store/modules/Vorlagen/actions';
  import { ActionTypes as VariablenActionTypes } from '@/store/modules/Variablen/actions';
  import { ActionTypes as CommonActionTypes } from '@/store/modules/Common/actions';

  import TextbausteinContentBearbeiten from '@/components/Vorlagen/TextbausteinContentBearbeiten.vue';
  import LoadingSpinner from '@/components/LoadingSpinner.vue';
  import SelectVariablenSidebar from '@/components/Vorlagen/SelectVariablenSidebar.vue';
  import ConfirmationAlert from '@/components/ConfirmationAlert.vue';
  // import TextbausteinAbschnitteRegelnModal from '@/components/Vorlagen/TextbausteinAbschnitteRegelnModal.vue';
  import LoadingOverlay from '@/components/LoadingOverlay.vue';

  import { Textbaustein, DeleteTextbausteinData } from '@/models/VorlageModels';
  //#endregion

  const props = defineProps({
    textbausteinid: {
      type: String as PropType<string>,
      default: '0',
    },
  });

  const store = useStore();
  const router = useRouter();

  const variablen = computed(() => store.getters.variablen);
  function loadVariablen() {
    store.dispatch(VariablenActionTypes.GetVariablen, undefined);
  }

  const loading = computed(
    () => store.getters.status.createTextbausteinLoading || store.getters.status.updateTextbausteinLoading || store.getters.status.deleteTextbausteinLoading
  );
  const errorMsg = computed(() => store.getters.status.textbausteinErrorMsg);
  const ownErrorMsg = ref('');

  const textbaustein = computed(
    () => store.getters.textbausteine.find((textbaustein: Textbaustein) => textbaustein.id == Number.parseInt(props.textbausteinid)) || new Textbaustein()
  );

  const editTextbaustein = ref<Textbaustein>(
    textbaustein.value != null ? (JSON.parse(JSON.stringify(textbaustein.value)) as Textbaustein) : new Textbaustein()
  );

  //#region Abschnitte
  function addAbschnitt() {
    const abschnitt = new Textbaustein();
    abschnitt.position = editTextbaustein.value.abschnitte.length + 1;
    editTextbaustein.value.abschnitte.push(abschnitt);
  }

  const confirmRemoveAbschnittIndex = ref<number | null>(null);

  function removeAbschnitt(index: number, force: boolean = false) {
    const removeAbschnitt = editTextbaustein.value.abschnitte[index];
    if (removeAbschnitt != undefined && removeAbschnitt.id != 0 && !force) {
      // confirm
      confirmRemoveAbschnittIndex.value = index;
      confirmationAlertRef.value?.showAlert(
        'Abschnitt löschen',
        'Möchten Sie den Abschnitt lösche?<br><br>Alle Verträge, die diesen Abschnitt verwenden, werden angepasst.',
        'removeAbschnitt',
        '',
        'danger',
        'text-white'
      );
    } else if (removeAbschnitt != undefined) {
      confirmRemoveAbschnittIndex.value = null;

      editTextbaustein.value.abschnitte.sort((a: Textbaustein, b: Textbaustein) => a.position - b.position).splice(index, 1);

      editTextbaustein.value.abschnitte.forEach((abschnitt, idx) => {
        abschnitt.position = idx + 1;
      });
    }
  }

  function abschnittPosUp(index: number) {
    editTextbaustein.value.abschnitte[index - 1].position++;
    editTextbaustein.value.abschnitte[index].position--;
  }

  function abschnittPosDown(index: number) {
    editTextbaustein.value.abschnitte[index + 1].position--;
    editTextbaustein.value.abschnitte[index].position++;
  }

  function validateAbschnittPositions() {
    if (editTextbaustein.value.abschnitte != null && editTextbaustein.value.abschnitte.length > 0) {
      const positionen = editTextbaustein.value.abschnitte.map((abschnitt) => abschnitt.position);
      // check for duplicates
      const hasDuplicates = positionen.some((pos, idx) => positionen.indexOf(pos) !== idx);
      if (hasDuplicates) {
        // reassign positions
        editTextbaustein.value.abschnitte.forEach((abschnitt, idx) => {
          abschnitt.position = idx + 1;
        });
      }
    }
  }
  //#endregion

  //#region Save and Cancel
  function sanitizeHTML(htmlString: string): string {
    // Regular expression to remove <script> tags and JavaScript code
    const sanitizedHTML = htmlString.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    return sanitizedHTML;
  }

  function nameAlreadyUsedValidation(name: string): boolean {
    return store.getters.textbausteine.some(
      (baustein: Textbaustein) =>
        (baustein.ownerid == null || baustein.ownerid == 0) && baustein.name.trim() === name.trim() && baustein.id != Number.parseInt(props.textbausteinid)
    );
  }

  function createOrUpdateTextbaustein(close: boolean) {
    ownErrorMsg.value = '';

    if (editTextbaustein.value.name.trim() == '') {
      ownErrorMsg.value = 'Der Name des Textbausteins darf nicht leer sein.';
      return;
    } else if (nameAlreadyUsedValidation(editTextbaustein.value.name)) {
      ownErrorMsg.value = 'Der Name des Textbausteins ist bereits vergeben.';
      return;
    }

    editTextbaustein.value.text = sanitizeHTML(editTextbaustein.value.text);
    editTextbaustein.value.gruppe = editTextbaustein.value.gruppe.trim();

    let abschnittError = false;
    editTextbaustein.value.abschnitte.forEach((abschnitt: Textbaustein) => {
      if (abschnitt.name.trim() == '') {
        abschnittError = true;
      }

      abschnitt.text = sanitizeHTML(abschnitt.text);
      abschnitt.gruppe = editTextbaustein.value.gruppe;
    });

    if (abschnittError) {
      ownErrorMsg.value = 'Der Name des Abschnitts darf nicht leer sein.';
      return;
    }

    if (textbaustein.value.id != 0) {
      store.dispatch(VorlageActionTypes.UpdateTextbaustein, editTextbaustein.value).then(() => {
        if (errorMsg.value == '') {
          cancel(close);
        }
      });
    } else {
      store.dispatch(VorlageActionTypes.CreateTextbaustein, editTextbaustein.value).then(() => {
        if (errorMsg.value == '') {
          cancel(close);
        }
      });
    }
  }

  function cancel(close: boolean = true) {
    editTextbaustein.value = textbaustein.value != null ? (JSON.parse(JSON.stringify(textbaustein.value)) as Textbaustein) : new Textbaustein();

    if (close) {
      store.dispatch(CommonActionTypes.OpenSidebar, undefined);

      router.back();
    }
  }
  //#endregion

  //#region DELETE
  const confirmationAlertRef = ref();

  function deleteTextbausteinRequest() {
    confirmationAlertRef.value?.showAlert('Textbaustein löschen', 'Möchten Sie den Textbaustein unwiderruflich löschen?', 'delete', '', 'danger', 'text-white');
  }

  function forceDeleteTextbausteinRequest() {
    confirmationAlertRef.value?.showAlert(
      'Textbaustein löschen',
      errorMsg.value +
        `<br><br>Möchten Sie den Textbaustein trotzdem löschen?<br><br>Dadurch werden die Vorlagen, die diesen Textbaustein verwenden, und alle auf diesen Vorlagen basierenden Verträge angepasst.`,
      'forceDelete',
      '',
      'danger',
      'text-white'
    );
  }

  function onConfrim(param: any) {
    if (param === 'delete') {
      deleteTextbaustein();
    } else if (param === 'forceDelete') {
      deleteTextbaustein(true);
    } else if (param === 'removeAbschnitt') {
      removeAbschnitt(confirmRemoveAbschnittIndex.value as number, true);
    } else {
      cancel();
    }
  }

  function deleteTextbaustein(forceDelete: boolean = false) {
    var request = new DeleteTextbausteinData(Number.parseInt(props.textbausteinid), forceDelete);

    store.dispatch(VorlageActionTypes.DeleteTextbaustein, request).then(() => {
      if (errorMsg.value == '') {
        cancel();
      } else if (errorMsg.value.includes('da er in folgenden Vorlagen verwendet wird')) {
        forceDeleteTextbausteinRequest();
      } else if (errorMsg.value.includes('da er in Verträgen verwendet wird')) {
        forceDeleteTextbausteinRequest();
      }
    });
  }
  //#endregion

  onMounted(() => {
    // close Sidebar
    store.dispatch(CommonActionTypes.CloseSidebar, undefined);
    // scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });

    validateAbschnittPositions();

    if (variablen.value.length == 0) {
      loadVariablen();
    }
  });
</script>

<style scoped>
  .save-bar {
    z-index: 1;
  }

  .textbaustein-abschnitt-bg {
    background-color: #f8f8f8;
  }

  #edit-textbaustein-view {
    margin-bottom: 70px;
  }

  .with-right-sidebar {
    padding-right: 235px;
  }
</style>
